/*
 * File: show-child-exists.ts
 * File Created: 2024-01-03 09:06:09
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-01-31 09:41:43
 * Modified By: Val Liu
 * -----
 */

import { Vue } from "vue-property-decorator";

/// This custom directive provides a declarative way to dynamically show or hide an element based on its child elements count, enhancing the reactivity and interactivity of Vue applications.
Vue.directive("show-child-exists", {
    bind: (el, binding, vnode) => {
        let display = el.style.display;

        // Function to count non-comment child nodes
        const countNonCommentChildNodes = (element) => {
            return Array.from(element.childNodes).filter(
                (child: any) => child.nodeType !== Node.COMMENT_NODE,
            ).length;
        };

        // Function to count eligible child nodes (ignoring comments and certain divs)
        const countEligibleChildNodes = (element) => {
            return Array.from(element.childNodes).filter((child: any) => {
                // Ignore comment nodes
                if (child.nodeType === Node.COMMENT_NODE) {
                    return false;
                }

                // Ignore div.vue-portal-target without children
                if (
                    child.nodeType === Node.ELEMENT_NODE &&
                    child.nodeName === "DIV" &&
                    child.classList.contains("vue-portal-target") &&
                    countNonCommentChildNodes(child) === 0
                ) {
                    return false;
                }

                return true;
            }).length;
        };

        const triggerDisplay = () => {
            const eligibleChildCount = countEligibleChildNodes(el);
            if (eligibleChildCount <= 0) el.style.display = "none";
            else el.style.display = display;
        };

        // Attach the observer to the element
        (el as any)._showChildExistsObserver = new MutationObserver(triggerDisplay);
        (el as any)._showChildExistsObserver.observe(el, {
            childList: true,
            subtree: true,
        });

        // Delay the initial triggerDisplay until after the DOM has updated
        vnode.context.$nextTick(() => {
            triggerDisplay();
        });
    },
    unbind: (el) => {
        // Access and disconnect the observer
        if ((el as any)._showChildExistsObserver) {
            (el as any)._showChildExistsObserver.disconnect();
            delete (el as any)._showChildExistsObserver;
        }
    },
});
